import {useMemo} from 'react';
import type {ProTipContext} from '../components/ai-prompt-box-pro-tip/ai-prompt-box-pro-tip.types';
import type {ChatMessage, ChatMessageTextPart} from '@Components/ai-chat-window/ai-chat-window.types';
import {ChatMessagePartType, MessageRole} from '@Components/ai-chat-window/ai-chat-window.types';
import type {UserImageAttachment} from '../components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import type {PromptAttachment} from '../components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PromptAttachmentType} from '../components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';

interface UseProTipContextParams {
  messages: Record<string, ChatMessage>;
  chatId?: string;
  attachmentItems: PromptAttachment[];
  isNewChat?: boolean;
  hasLoadingAttachments?: boolean;
  isMessageInProgress?: boolean;
}

export function useProTipContext({messages, chatId, attachmentItems, isNewChat = false, hasLoadingAttachments = false, isMessageInProgress = false}: UseProTipContextParams): ProTipContext {
  return useMemo((): ProTipContext => {
    const messageValues = Object.values(messages).filter((msg) => !msg.deletedAt);
    const userMessages = messageValues.filter((msg) => msg.role === MessageRole.USER);
    const userMessageCount = userMessages.length;
    const hasGeneratedDesign = messageValues.some(
      (msg) => msg.role === MessageRole.ASSISTANT && msg.parts.some((part) => part.type === ChatMessagePartType.MODEL_GENERATED_IMAGE && !part.isSkeletonPlaceholder)
    );

    const firstUserMessageLength = userMessageCount > 0
      ? userMessages[0].parts
          .filter((part): part is ChatMessageTextPart => part.type === ChatMessagePartType.TEXT)
          .map((part) => part.text)
          .join('')
          .length
      : undefined;

    const lastImageAttachment = [...attachmentItems]
      .reverse()
      .find((a): a is UserImageAttachment => a.type === PromptAttachmentType.IMAGE || a.type === PromptAttachmentType.CAPTURE_PHOTO);

    const lastAttachmentDimensions =
      lastImageAttachment?.width && lastImageAttachment?.height
        ? {width: lastImageAttachment.width, height: lastImageAttachment.height}
        : undefined;

    return {
      userMessageCount,
      hasGeneratedDesign,
      chatId,
      isFirstMessage: userMessageCount === 0,
      isNewChat,
      isMessageInProgress,
      firstUserMessageLength,
      hasLoadingAttachments,
      lastAttachmentDimensions,
    };
  }, [messages, chatId, attachmentItems, isNewChat, hasLoadingAttachments, isMessageInProgress]);
}
